import { useEffect, useRef } from 'react';

/**
 * Tawk.to live chat integration for Syllera.
 * - Loads the widget when REACT_APP_TAWK_PROPERTY_ID and REACT_APP_TAWK_WIDGET_ID are set.
 * - Sets visitor name/email from logged-in user (localStorage) for better support context.
 * - Email notifications and offline form are configured in the Tawk.to dashboard.
 * - Rendered in App.js (all routes) and inside UserDashboard so the widget loads on student pages.
 */
const TAWK_PROPERTY_ID = process.env.REACT_APP_TAWK_PROPERTY_ID || '';
const TAWK_WIDGET_ID = process.env.REACT_APP_TAWK_WIDGET_ID || '';

function TawkTo() {
  const loaded = useRef(false);

  useEffect(() => {
    if (!TAWK_PROPERTY_ID || !TAWK_WIDGET_ID || loaded.current) return;
    // Avoid double injection if script was already added (e.g. by another TawkTo instance)
    if (document.querySelector('script[src*="embed.tawk.to"]')) return;
    loaded.current = true;

    const user = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        return {};
      }
    })();

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Best practice: fixed position bottom-right (industry standard for chat widgets)
    window.Tawk_API.customStyle = {
      visibility: {
        desktop: { position: 'br', xOffset: 20, yOffset: 20 },
        mobile: { position: 'br', xOffset: 10, yOffset: 10 }
      }
    };

    // Set visitor attributes when widget loads (name, email for logged-in users)
    window.Tawk_API.onLoad = function () {
      if (user.email || user.name) {
        window.Tawk_API.setAttributes(
          {
            name: user.name || user.fullName || user.email || 'Visitor',
            email: user.email || ''
          },
          (err) => {
            if (err) console.warn('Tawk setAttributes:', err);
          }
        );
      }

      // Start minimized so it doesn't pop open automatically.
      // Users can open it by clicking the chat bubble.
      try {
        // Some accounts/widgets may open by default depending on dashboard settings.
        // Calling minimize() here forces the default collapsed state.
        setTimeout(() => {
          if (window.Tawk_API && typeof window.Tawk_API.minimize === 'function') {
            window.Tawk_API.minimize();
          }
        }, 0);
      } catch {
        // ignore
      }
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    const first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(script, first);
  }, []);

  return null;
}

/**
 * Opens the Tawk.to chat widget (e.g. from "Start chat" buttons).
 * Safe to call even if Tawk is not configured.
 */
export function openTawkChat() {
  if (typeof window !== 'undefined' && window.Tawk_API) {
    window.Tawk_API.maximize();
  }
}

export default TawkTo;
